// src/components/Dropdowns.js
import { useEffect, useRef, useState } from "react";
import api from "../api.js";
import { useT } from "../i18n/LanguageContext.js";

/**
 * Props:
 *  - onNext: (selection) => void
 *  - productType?: string  // from route or query
 */
export default function Dropdowns({ onNext, productType }) {
  const t = useT();

  // selection values
  const [year, setYear] = useState("");
  const [make, setMake] = useState("");
  const [model, setModel] = useState("");
  const [submodel, setSubmodel] = useState("");

  // option lists
  const [years, setYears] = useState([]);
  const [makes, setMakes] = useState([]);
  const [models, setModels] = useState([]);
  const [submodels, setSubmodels] = useState([]);

  // parts
  const [parts, setParts] = useState([]);
  const [selectedPart, setSelectedPart] = useState(null);

  // ui state
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const alertRef = useRef(null);

  useEffect(() => {
    if (err && alertRef.current) alertRef.current.focus();
  }, [err]);

  // resets
  const resetAfterYear = () => {
    setMake(""); setModel(""); setSubmodel("");
    setMakes([]); setModels([]); setSubmodels([]);
    setParts([]); setSelectedPart(null);
  };
  const resetAfterMake = () => {
    setModel(""); setSubmodel("");
    setModels([]); setSubmodels([]);
    setParts([]); setSelectedPart(null);
  };
  const resetAfterModel = () => {
    setSubmodel("");
    setSubmodels([]);
    setParts([]); setSelectedPart(null);
  };
  const resetAfterSubmodel = () => {
    setParts([]); setSelectedPart(null);
  };

  // helper to append ?productType=... if provided
  const withPT = (path) =>
    productType ? `${path}?productType=${encodeURIComponent(productType)}` : path;

  // load years
  useEffect(() => {
    setLoading(true);
    api.get(withPT("/dropdowns/year"))
      .then((r) => setYears(r.data))
      .catch(() => setErr(t("alerts.loadYearsFail")))
      .finally(() => setLoading(false));
  }, [t, productType]);

  // load makes
  useEffect(() => {
    if (!year) return;
    setLoading(true);
    api.get(withPT(`/dropdowns/make/${year}`))
      .then((r) => setMakes(r.data))
      .catch(() => setErr(t("alerts.loadMakesFail")))
      .finally(() => setLoading(false));
  }, [year, t, productType]);

  // load models
  useEffect(() => {
    if (!year || !make) return;
    setLoading(true);
    api.get(withPT(`/dropdowns/model/${year}/${make}`))
      .then((r) => setModels(r.data))
      .catch(() => setErr(t("alerts.loadModelsFail")))
      .finally(() => setLoading(false));
  }, [year, make, t, productType]);

  // load submodels
  useEffect(() => {
    if (!year || !make || !model) return;
    setLoading(true);
    api.get(withPT(`/dropdowns/submodel/${year}/${make}/${model}`))
      .then((r) => setSubmodels(r.data))
      .catch(() => setErr(t("alerts.loadSubmodelsFail")))
      .finally(() => setLoading(false));
  }, [year, make, model, t, productType]);

  // load parts
  useEffect(() => {
    if (!year || !make || !model || !submodel) return;
    setLoading(true);
    api.get(withPT(`/dropdowns/part/${year}/${make}/${model}/${submodel}`))
      .then((r) => {
        const list = r.data || [];
        setParts(list);
        setSelectedPart(list.length === 1 ? list[0] : null); // auto-select if only one
      })
      .catch(() => setErr(t("alerts.loadPartsFail")))
      .finally(() => setLoading(false));
  }, [year, make, model, submodel, t, productType]);

  // submit
  const goNext = (e) => {
    e.preventDefault();
    setErr("");
    if (!selectedPart) {
      setErr(t("alerts.selectPart"));
      return;
    }
    onNext({
      year,
      make,
      model,
      submodel,
      ProductType: selectedPart.ProductType || productType || null,
      PartNumber: selectedPart.PartNumber || null,
      Availability: selectedPart.Availability ?? null,
      FitmentNotes: selectedPart.FitmentNotes, // ← add this line

    });
  };

  return (
    <form onSubmit={goNext} noValidate>
      <h2 style={{ marginTop: 0 }}>{t("step2Title")}</h2>

      {/* Active filter hint */}
      {productType && (
        <p style={{ marginTop: -6, marginBottom: 12, color: "var(--muted)" }}>
          {t("field.productType")}: <strong>{productType}</strong>
        </p>
      )}

      {err && (
        <div ref={alertRef} role="alert" className="alert" tabIndex="-1">
          {err}
        </div>
      )}

      {/* Vehicle selector */}
      <section className="card" style={{ marginBottom: 16 }}>
        <fieldset>
          <legend>{t("sections.vehicle")}</legend>

          <div className="grid-2">
            <div className="field">
              <label htmlFor="year">
                {t("field.year")}
                <span aria-hidden="true">{t("req")}</span>
              </label>
              <select
                id="year"
                value={year}
                onChange={(e) => { setYear(e.target.value); resetAfterYear(); }}
                required
              >
                <option value="">{t("field.year")}</option>
                {years.map((y) => <option key={y} value={y}>{y}</option>)}
              </select>
            </div>

            {makes.length > 0 && (
              <div className="field">
                <label htmlFor="make">
                  {t("field.make")}
                  <span aria-hidden="true">{t("req")}</span>
                </label>
                <select
                  id="make"
                  value={make}
                  onChange={(e) => { setMake(e.target.value); resetAfterMake(); }}
                  required
                >
                  <option value="">{t("field.make")}</option>
                  {makes.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            )}

            {models.length > 0 && (
              <div className="field">
                <label htmlFor="model">
                  {t("field.model")}
                  <span aria-hidden="true">{t("req")}</span>
                </label>
                <select
                  id="model"
                  value={model}
                  onChange={(e) => { setModel(e.target.value); resetAfterModel(); }}
                  required
                >
                  <option value="">{t("field.model")}</option>
                  {models.map((m) => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>
            )}

            {submodels.length > 0 && (
              <div className="field">
                <label htmlFor="submodel">
                  {t("field.submodel")}
                  <span aria-hidden="true">{t("req")}</span>
                </label>
                <select
                  id="submodel"
                  value={submodel}
                  onChange={(e) => { setSubmodel(e.target.value); resetAfterSubmodel(); }}
                  required
                >
                  <option value="">{t("field.submodel")}</option>
                  {submodels.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            )}
          </div>
        </fieldset>

        <div className="status" role="status" aria-live="polite">
          {loading ? t("status.loadingOptions") : ""}
        </div>
      </section>

      {/* Parts list */}
      {submodel && (
        <section className="card" style={{ marginBottom: 16 }}>
          <h3 style={{ marginTop: 0 }}>{t("parts.heading")}</h3>

          {parts.length === 0 && !loading && <p>{t("parts.none")}</p>}

          {parts.length > 0 && (
            <div style={{ overflowX: "auto" }}>
              <table>
                <caption className="sr-only">{t("parts.tableCaption")}</caption>
                <thead>
                    <tr>
                        <th scope="col">{t("parts.select")}</th>
                        <th scope="col">{t("field.productType")}</th>
                        <th scope="col">{t("field.partNumber")}</th>
                        <th scope="col">{t("field.availability")}</th>
                        <th scope="col">{t("field.fitmentNotes")}</th>
                    </tr>
                    </thead>
                <tbody>
                  {parts.map((p, i) => {
                    const id = `part-${i}-${p.PartNumber}`;
                    const checked =
                      selectedPart?.PartNumber === p.PartNumber &&
                      selectedPart?.ProductType === p.ProductType;
                    return (
                      <tr key={id}>
                        <td>
                          <input
                            type="radio"
                            name="selectedPart"
                            id={id}
                            checked={checked}
                            onChange={() => setSelectedPart(p)}
                          />
                        </td>
                        <td><label htmlFor={id}>{p.ProductType}</label></td>
                        <td>{p.PartNumber}</td>
                        <td>{typeof p.Availability === "number" ? p.Availability : (p.Availability ?? "")}</td>
                        <td style={{ whiteSpace: "pre-wrap", maxWidth: "400px" }}>
                            {p.FitmentNotes || ""}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </section>
      )}

      <div className="btn-row">
        <button
          className="btn primary"
          type="submit"
          disabled={!submodel || !selectedPart}
          aria-disabled={!submodel || !selectedPart}
        >
          {t("btn.review")}
        </button>
        <button
          className="btn ghost"
          type="button"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        >
          {t("btn.top")}
        </button>
      </div>
    </form>
  );
}
